import GatewayPortalV2 from "@soenergy/frontend-library/src/services/api/GatewayPortalV2"
import AuthenticationService from "@soenergy/frontend-library/src/services/AuthenticationService"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import type { Router } from "vue-router"
import type { Store } from "vuex"

/**
 * So Energy's chat widget (customer-support-center `chat-widget/`), the app's only chat. It loads
 * from `VUE_APP_SO_CHAT_WIDGET_LOADER`, set per environment at build time; unset, there is no chat.
 * Each build of the loader is pinned to its environment's widget API, so a staging build can never
 * open a chat on the production Connect instance. The same widget runs on Nexus (fe-nexus
 * `src/plugins/soChat.ts`); both serve www.so.energy/myaccount, so a chat carries on across them.
 *
 * Signed in, a chat is started by Portal V2 (`POST /api/v1/accounts/{accountId}/chat`), which
 * checks the session and account ownership and starts the chat authenticated, so the assistant
 * skips ID&V. Signed out, `startChat` resolves null and the widget starts an anonymous chat itself.
 */
export interface StartChatDetails {
  contactId: string
  participantId: string
  participantToken: string
}

interface SoChatIdentity {
  verified: boolean
  name?: string
}

type SoChatFn = ((command: string, arg?: unknown) => void) & { q?: unknown[][] }
type SoChatWindow = Window & { SoChat?: SoChatFn }

/** The bits of the my-account store this plugin reads. */
interface ChatState {
  currentAccount: number | string | null
  user?: { customer?: { forename?: string } }
  authentication?: { accessToken?: string | null }
}

/** Set on the catch-all route (router.js): a link only it matches is not ours to handle. */
export const CATCH_ALL_META = "soChatCatchAll"

/**
 * Whether there is a session right now. The access token lives in memory only, so after a reload
 * this uses the refresh cookie first; the store's persisted `currentAccount` outlives the session
 * and says nothing about it. A visitor with no refresh cookie waits for nothing.
 */
const hasSession = (): Promise<boolean> =>
  AuthenticationService.checkIfAuthenticatedAndFetchTokenIfNeeded()

/** The current account as a comparable key: the API and the account picker disagree on type. */
const accountKeyOf = (state: ChatState): string | null =>
  state.currentAccount ? String(state.currentAccount) : null

export const loaderUrl = (): string =>
  (process.env.VUE_APP_SO_CHAT_WIDGET_LOADER ?? "").trim()

export const isSoChatEnabled = (): boolean => loaderUrl() !== ""

/** The widget's command queue, the same stub its embed snippet defines; the loader replays it. */
const soChat = (command: string, arg?: unknown): void => {
  const w = window as SoChatWindow
  if (!w.SoChat) {
    const stub: SoChatFn = (...args) => {
      stub.q ??= []
      stub.q.push(args)
    }
    w.SoChat = stub
  }
  w.SoChat(command, arg)
}

/**
 * A start Portal V2 refused. The widget reads `status` and `code` off the rejection: 401 with
 * `session_expired` shows its sign-in message, 429 its busy state, anything else a generic error.
 */
export class ChatStartRefused extends Error {
  constructor(readonly status: number, readonly code?: string) {
    super(code ?? `Chat start refused (${status})`)
    this.name = "ChatStartRefused"
  }
}

/**
 * Called by the widget on every press of Start. Signed in: the account the customer is looking at
 * right now. Otherwise null, so the chat is anonymous; `onSignedOut` then puts the badge right if
 * the session ended without a log out (it is never called with a session).
 */
export const startChatWith =
  (store: Store<ChatState>, onSignedOut: () => void = () => undefined) =>
  async (): Promise<StartChatDetails | null> => {
    const accountId = store.state.currentAccount
    if (!accountId || !(await hasSession())) {
      onSignedOut()
      return null
    }
    try {
      const { data } = await GatewayPortalV2.startAccountChat(accountId)
      return data
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status === 401) throw new ChatStartRefused(401, "session_expired")
      if (status === 429) throw new ChatStartRefused(429)
      throw error
    }
  }

/** Same-origin links in replies open in the app, without a page load, when the router knows them. */
export const navigateWith =
  (router: Router, base: string = process.env.BASE_URL ?? "/") =>
  (url: string): boolean => {
    let target: URL
    try {
      target = new URL(url, window.location.href)
    } catch {
      return false
    }
    if (target.origin !== window.location.origin) return false
    const prefix = base.replace(/\/$/, "")
    const inApp =
      !prefix ||
      target.pathname === prefix ||
      target.pathname.startsWith(`${prefix}/`)
    if (!inApp) return false
    const resolved = router.resolve(
      (target.pathname.slice(prefix.length) || "/") +
        target.search +
        target.hash
    )
    const isCatchAll = resolved.matched.some(
      (record) => record.meta?.[CATCH_ALL_META]
    )
    if (!resolved.matched.length || isCatchAll) return false
    void router.push(resolved.fullPath)
    return true
  }

/** The badge for a signed-in customer: their first name when the store has it. */
export const identityOf = (state: ChatState): SoChatIdentity => {
  const name = state.user?.customer?.forename?.trim()
  return name ? { verified: true, name } : { verified: true }
}

/**
 * Once a log out has succeeded, before the page reloads or redirects: forget the customer and end
 * a live signed-in chat, so the next person on this device cannot see it.
 */
export const endSoChatForSignOut = (): void => {
  if (!isSoChatEnabled()) return
  soChat("update", { identity: { verified: false } })
  soChat("end")
}

/**
 * Loads the widget once and keeps its identity in step with the session.
 *
 * The session is resolved before the widget is told anything: the widget resumes a stored chat as
 * soon as its panel mounts, and resumes a signed-in chat only if the host says the customer is
 * signed in at that moment. The store's persisted account alone would vouch for a customer whose
 * session has gone (an expired refresh cookie), so it is never taken as a sign-in by itself.
 *
 * Afterwards the store is followed: a sign-in (a session and an account) sets the badge, losing
 * either clears it, and a switch to another account ends the chat, because a chat is authenticated
 * for the account it started on and must not carry on while the customer looks at another. Log out
 * itself ends the chat through `endSoChatForSignOut`.
 */
export const initSoChat = async (
  store: Store<ChatState>,
  router: Router
): Promise<void> => {
  const session = await hasSession()
  // The account the widget has been told the customer is signed in to, or null when signed out.
  let signedInAccount = session ? accountKeyOf(store.state) : null

  const signOut = () => {
    if (signedInAccount === null) return
    signedInAccount = null
    soChat("update", { identity: { verified: false } })
  }

  soChat("init", {
    startChat: startChatWith(store, signOut),
    navigate: navigateWith(router),
    ...(signedInAccount && { identity: identityOf(store.state) }),
  })

  store.watch(
    (state) =>
      [
        accountKeyOf(state),
        Boolean(state.authentication?.accessToken),
        state.user?.customer?.forename,
      ] as const,
    ([account, hasToken]) => {
      if (!account || !hasToken) {
        signOut()
        return
      }
      if (signedInAccount && account !== signedInAccount) soChat("end")
      signedInAccount = account
      soChat("update", { identity: identityOf(store.state) })
    }
  )

  const script = document.createElement("script")
  script.src = loaderUrl()
  script.async = true
  script.onerror = () =>
    ErrorReporter.report(
      new Error("So Energy chat widget loader failed to load"),
      { src: script.src }
    )
  document.head.appendChild(script)
}
